import {  UpdateLastActivityDateDocument } from "generated/resources/graphql"
import { useMutation } from "modules/core/hooks/useMutation"
import app from "modules/core/store/models/app"
import { useCallback, useRef } from "react"
import i18n from 'modules/core/lib/i18n'

interface UseContentActionsTrackingProps {
  trainingId: string | undefined
  employeeId: number
  enabled?: boolean // Don't track if training is completed
}

const TOAST_TIME = 2000

export const useContentActionsTracking = ({
  trainingId,
  employeeId,
  enabled = true
}: UseContentActionsTrackingProps) => {

  const [updateLastActivityDate] = useMutation(UpdateLastActivityDateDocument, {
    extractorFn: (mutationData) =>
      mutationData.trainingsMutations.updateLastActivityDateTrainingsContentActivityLog,
  })

//lastCallRef: Stores the timestamp of the last API call
// DEBOUNCE_MS: 5-second cooldown period between API calls to prevent excessive API calls
// Why? Prevents API spam if user rapidly clicks Next/Play/Pause multiple times
  const lastCallRef = useRef<number>(0)
  const DEBOUNCE_MS = 5000

  // Store enabled value in a ref, to get latest value, because when course reaches the completed page this needs to be updated to enabled:false
  const enabledRef = useRef(enabled)
  enabledRef.current = enabled

  const trackAction = useCallback(async () => {
    const currentEnabled = enabledRef.current
    if (!currentEnabled || !trainingId) {
      return
    }

    const now = Date.now()
    if (now - lastCallRef.current < DEBOUNCE_MS) {
      return // Skip if called too recently
    }

    lastCallRef.current = now

    try {
      await updateLastActivityDate({
        variables: { trainingId: Number(trainingId), employeeId }
      })
    } catch (error) {
      // Silent fail - don't interrupt user experience
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
        time: TOAST_TIME,
      })
    }
  }, [trainingId, employeeId, updateLastActivityDate])

  return { trackAction }
}